'use client'

import * as React from 'react'
import { cva } from 'class-variance-authority'
import { Swiper, SwiperSlide } from 'swiper/react'
import { FreeMode, Mousewheel, Pagination } from 'swiper/modules'
import { useMediaQuery } from 'react-responsive'
import 'swiper/css/pagination'
import { cn } from '../../helpers/index.js'
import type {
  MediaEndorsementProps,
  MediaEndorsementData,
  MediaEndorsementItem,
  MediaEndorsementSemanticName,
} from './types.js'
import { Heading, Text } from '../../index.js'
import { withLayout } from '../../shared/Styles.js'

const MOBILE_ITEMS_PER_SLIDE = 3

// Helper to chunk array into groups
function chunkArray<T>(array: T[], size: number): T[][] {
  const result: T[][] = []
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size))
  }
  return result
}

/**
 * 卡片容器变体
 */
const cardVariants = cva(
  'rounded-box lg-desktop:h-[240px] desktop:px-6 bg-container-secondary-0 flex h-[200px] flex-col justify-start gap-[26px] p-4',
  {
    variants: {
      theme: {
        light: '',
        dark: '',
      },
      rounded: {
        true: 'rounded-card',
        false: '',
      },
    },
    defaultVariants: {
      theme: 'light',
      rounded: false,
    },
  }
)

/**
 * MediaEndorsement - 媒体背书
 *
 * @description 媒体背书组件的作用是通过展示第三方媒体的评价或报道，提升品牌或产品的可信度和影响力
 */
const MediaEndorsement = React.forwardRef<HTMLDivElement, MediaEndorsementProps>(
  ({ className, classNames = {}, data, onItemClick, ...props }, ref) => {
    const { title, subtitle, items, theme = 'light', rounded = false, layout = 'grid' } = data
    const isMobile = useMediaQuery({ query: '(max-width: 767px)' })

    const handleCardClick = React.useCallback(
      (item: MediaEndorsementItem, index: number) => (event: React.MouseEvent) => {
        onItemClick?.(item, index, event)
      },
      [onItemClick]
    )

    const renderCard = (item: MediaEndorsementItem, index: number) => (
      <div
        key={index}
        className={cn('media-endorsement-card text-info-primary', cardVariants({ theme, rounded }), classNames.card)}
        onClick={handleCardClick(item, index)}
        aria-label={item.mediaName ? `${item.mediaName} endorsement` : undefined}
      >
        {/* 媒体 Logo */}
        <div
          className={cn(
            'media-endorsement-card-icon rounded-full',
            'lg-desktop::h-14 flex h-12 w-auto  items-center overflow-hidden',
            classNames.cardIcon
          )}
        >
          <img
            src={item.icon.url}
            alt={item.icon.alt || item.mediaName || 'Media logo'}
            className="lg-desktop::h-12 h-[42px] w-auto object-contain"
            loading="lazy"
          />
        </div>

        {/* 引用文案 */}
        <Heading size={2} as={'h6'} className={cn('media-endorsement-card-quote line-clamp-4', classNames.cardQuote)}>
          {item.text}
        </Heading>
      </div>
    )

    // Mobile swiper for grid layout
    const renderMobileGridSwiper = () => {
      const chunkedItems = chunkArray(items, MOBILE_ITEMS_PER_SLIDE)
      return (
        <Swiper
          className={cn('media-endorsement-mobile-swiper', 'w-full !overflow-visible pb-8', classNames.carousel)}
          modules={[Pagination]}
          slidesPerView={1}
          spaceBetween={12}
          pagination={{
            clickable: true,
            bulletClass: 'media-endorsement-bullet',
            bulletActiveClass: 'media-endorsement-bullet-active',
          }}
        >
          {chunkedItems.map((group, slideIndex) => (
            <SwiperSlide key={slideIndex} className="!h-auto">
              <div className="flex flex-col gap-3">
                {group.map((item, itemIndex) => {
                  const globalIndex = slideIndex * MOBILE_ITEMS_PER_SLIDE + itemIndex
                  return renderCard(item, globalIndex)
                })}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )
    }

    return (
      <div
        ref={ref}
        className={cn('media-endorsement-root text-info-primary', 'w-full', className, classNames.root, {
          'aiui-dark': theme === 'dark',
        })}
        {...props}
      >
        <style>{`
          .media-endorsement-bullet {
            display: inline-block;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #D9D9D9;
            margin: 0 4px;
            cursor: pointer;
            transition: background-color 0.3s ease;
          }
          .media-endorsement-bullet-active {
            background: var(--info-primary-color);
          }
          .media-endorsement-mobile-swiper .swiper-pagination {
            position: absolute;
            bottom: -28px;
            left: 0;
            right: 0;
            text-align: center;
          }
        `}</style>
        {/* 标题区域 */}
        {(title || subtitle) && (
          <div className={cn('media-endorsement-header mb-6', classNames.header)}>
            {title && (
              <Heading as="h2" size={4} html={title} className={cn('media-endorsement-title', classNames.title)} />
            )}
            {subtitle && <Text as="p" size={3} className={cn('', classNames?.subtitle)} html={subtitle} />}
          </div>
        )}
        {layout === 'carousel' ? (
          <Swiper
            className={cn('media-endorsement-carousel', 'w-full !overflow-visible', classNames.carousel)}
            modules={[FreeMode, Mousewheel]}
            freeMode={true}
            mousewheel={{ forceToAxis: true }}
            slidesPerView="auto"
            spaceBetween={12}
            breakpoints={{
              768: { spaceBetween: 16 },
            }}
          >
            {items.map((item, index) => (
              <SwiperSlide key={index} className="tablet:!w-[320px] laptop:!w-[360px] desktop:!w-[404px] !w-[280px]">
                {renderCard(item, index)}
              </SwiperSlide>
            ))}
          </Swiper>
        ) : isMobile ? (
          renderMobileGridSwiper()
        ) : (
          <div
            className={cn(
              'media-endorsement-grid',
              'laptop:grid-cols-2 laptop:gap-4 desktop:grid-cols-3 tablet:grid-cols-2 grid grid-cols-1 gap-3',
              classNames.grid
            )}
          >
            {items.map((item, index) => renderCard(item, index))}
          </div>
        )}
      </div>
    )
  }
)

MediaEndorsement.displayName = 'MediaEndorsement'

export default withLayout(MediaEndorsement)
export type { MediaEndorsementProps, MediaEndorsementData, MediaEndorsementItem, MediaEndorsementSemanticName }
